import { Controller } from 'stimulus'

export default class extends Controller {
  static targets = ['panel']

  connect() {
    this.storageKey = 'crm_grid_cols_v1'
    this.orderKey = 'crm_grid_order_v1'
    this.hidden = this.load()
    this.order = this.loadOrder()
    this.applyOrder()
    this.apply()
    this.syncCheckboxes()
    this.setupDrag()
    this.onDocClick = this.onDocClick.bind(this)
    document.addEventListener('click', this.onDocClick)
  }

  disconnect() {
    document.removeEventListener('click', this.onDocClick)
  }

  load() {
    try {
      const saved = localStorage.getItem(this.storageKey)
      if (saved) return JSON.parse(saved) || {}
    } catch (e) { /* fall through to defaults */ }
    const h = {}
    ;(this.data.get('defaultHidden') || '').split(',').filter(Boolean).forEach((c) => { h[c] = true })
    return h
  }

  save() {
    localStorage.setItem(this.storageKey, JSON.stringify(this.hidden))
  }

  togglePanel(e) {
    e.stopPropagation()
    if (this.hasPanelTarget) this.panelTarget.classList.toggle('hidden')
  }

  onDocClick(e) {
    if (!this.hasPanelTarget || this.panelTarget.classList.contains('hidden')) return
    if (this.panelTarget.contains(e.target)) return
    if (e.target.closest('[data-action~="crm-columns#togglePanel"]')) return
    this.panelTarget.classList.add('hidden')
  }

  toggle(e) {
    const col = e.target.dataset.colToggle
    if (!col) return
    this.hidden[col] = !e.target.checked
    this.save()
    this.applyColumn(col)
  }

  reset() {
    this.hidden = {}
    this.save()
    localStorage.removeItem(this.orderKey)
    this.order = this.currentDomOrder()
    this.element.querySelectorAll('[data-col-toggle]').forEach((cb) => { cb.checked = true })
    this.element.querySelectorAll('[data-col]').forEach((c) => c.classList.remove('hidden'))
  }

  apply() {
    Object.keys(this.hidden).forEach((col) => this.applyColumn(col))
  }

  applyColumn(col) {
    const off = !!this.hidden[col]
    this.element.querySelectorAll(`[data-col="${col}"]`).forEach((cell) => {
      cell.classList.toggle('hidden', off)
    })
  }

  syncCheckboxes() {
    this.element.querySelectorAll('[data-col-toggle]').forEach((cb) => {
      cb.checked = !this.hidden[cb.dataset.colToggle]
    })
  }

  currentDomOrder() {
    const headerRow = this.element.querySelector('thead tr')
    if (!headerRow) return []
    return Array.from(headerRow.querySelectorAll('th[data-col]')).map((th) => th.dataset.col)
  }

  loadOrder() {
    try {
      const saved = localStorage.getItem(this.orderKey)
      if (saved) {
        const arr = JSON.parse(saved)
        if (Array.isArray(arr) && arr.length) return this.reconcile(arr)
      }
    } catch (e) { /* fall through */ }
    return this.currentDomOrder()
  }

  reconcile(saved) {
    const dom = this.currentDomOrder()
    const kept = saved.filter((c) => dom.includes(c))
    dom.forEach((c) => { if (!kept.includes(c)) kept.push(c) })
    return kept
  }

  applyOrder() {
    if (!this.order || !this.order.length) return
    const headerRow = this.element.querySelector('thead tr')
    if (headerRow) this.applyRowOrder(headerRow)
    this.element.querySelectorAll('tbody tr[data-target="crm-grid.row"]').forEach((r) => this.applyRowOrder(r))
  }

  applyRowOrder(row) {
    if (!this.order || !this.order.length) return
    const anchor = row.lastElementChild
    const map = {}
    row.querySelectorAll('[data-col]').forEach((c) => { map[c.dataset.col] = c })
    this.order.forEach((col) => { if (map[col]) row.insertBefore(map[col], anchor) })
  }

  setupDrag() {
    this.element.querySelectorAll('thead th[data-col]').forEach((th) => {
      th.draggable = true
      th.classList.add('crm-col-drag')
    })
    this.element.addEventListener('dragstart', (e) => {
      const th = e.target.closest('thead th[data-col]')
      if (!th) return
      this.dragCol = th.dataset.col
      e.dataTransfer.effectAllowed = 'move'
      th.classList.add('opacity-40')
    })
    this.element.addEventListener('dragover', (e) => {
      const th = e.target.closest('thead th[data-col]')
      if (!th || !this.dragCol) return
      e.preventDefault()
      e.dataTransfer.dropEffect = 'move'
      th.classList.toggle('crm-col-drop', th.dataset.col !== this.dragCol)
    })
    this.element.addEventListener('dragleave', (e) => {
      const th = e.target.closest('thead th[data-col]')
      if (th) th.classList.remove('crm-col-drop')
    })
    this.element.addEventListener('drop', (e) => {
      const th = e.target.closest('thead th[data-col]')
      if (!th || !this.dragCol) return
      e.preventDefault()
      if (th.dataset.col !== this.dragCol) this.reorder(this.dragCol, th.dataset.col)
    })
    this.element.addEventListener('dragend', () => {
      this.element.querySelectorAll('.opacity-40, .crm-col-drop').forEach((el) => el.classList.remove('opacity-40', 'crm-col-drop'))
      this.dragCol = null
    })
  }

  reorder(from, to) {
    const arr = this.order.slice()
    const fi = arr.indexOf(from)
    const ti = arr.indexOf(to)
    if (fi === -1 || ti === -1) return
    arr.splice(fi, 1)
    const newTi = arr.indexOf(to)
    arr.splice(fi < ti ? newTi + 1 : newTi, 0, from)
    this.order = arr
    localStorage.setItem(this.orderKey, JSON.stringify(arr))
    this.applyOrder()
  }
}
